import { Button, cn } from '@up2stay/up2promo-ui';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ChevronLeft } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useDictionary } from '@/core/providers/dictionary-provider';

import { useOnboardingHeaderLeading } from './onboarding-layout';
import { OnboardingProgressBar } from './onboarding-progress-bar';

interface WizardShellProps {
  children: ReactNode;
  /** 1-indexed display step. */
  currentStep: number;
  totalSteps: number;
  /** Optional named labels for the progress strip. */
  stepLabels?: string[];
  /** -1 = moving back, 1 = moving forward. Drives AnimatePresence slide direction. */
  direction: number;
  /** Key identifying the current step; used as AnimatePresence key & scroll reset key. */
  stepKey: string | number;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /**
   * Shown above the floating CTA when the user taps it while disabled
   * (e.g. „Bitte wähle, wofür du up2promo nutzen möchtest.“).
   */
  nextDisabledHint?: string;
  isLoading?: boolean;
  /** Show the back button in the footer/top-bar. */
  showBack?: boolean;
  /** Show the back/next footer entirely. */
  showFooter?: boolean;
  /**
   * Optional content rendered in the top-right corner (e.g. logout button on
   * the onboarding page, close X in a dialog). When omitted, no top bar is
   * rendered unless a mobile back button is needed.
   */
  topRightAction?: ReactNode;
  /**
   * `page`   — mobile shows a floating bottom next-button overlay (for the
   *            full-page onboarding flow). Safe-area padding on the CTA.
   * `shell`  — floating CTA on **every** viewport (products / in-shell wizards).
   *            Stage width / padding still respond to breakpoints.
   * `dialog` — inline footer on every viewport (better inside a Dialog/Drawer).
   */
  variant?: 'page' | 'dialog' | 'shell';
  /** Vertical alignment for step content inside the scroll area. */
  contentAlignment?: 'center' | 'start';
  /** Extra classes on the step content (e.g. a wider max-width). */
  contentClassName?: string;
}

const fadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

const fadeTransition = {
  duration: 0.18,
  ease: [0.22, 1, 0.36, 1] as const,
};

const SWIPE_COMMIT_THRESHOLD = 80;

function useSwipeBack(onBack: (() => void) | undefined, enabled: boolean) {
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swiping = useRef(false);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      if (touch && touch.clientX < 30) {
        touchStart.current = { x: touch.clientX, y: touch.clientY };
        swiping.current = false;
      } else {
        touchStart.current = null;
      }
    },
    [enabled],
  );

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - touchStart.current.x;
    const dy = Math.abs(touch.clientY - touchStart.current.y);
    if (dx > 10 && dx > dy * 1.5) {
      swiping.current = true;
    }
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart.current || !swiping.current) {
        touchStart.current = null;
        return;
      }
      const touch = e.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - touchStart.current.x;
      if (dx > SWIPE_COMMIT_THRESHOLD) {
        onBack?.();
      }
      touchStart.current = null;
      swiping.current = false;
    },
    [onBack],
  );

  return { onTouchStart, onTouchMove, onTouchEnd };
}

export function WizardShell({
  children,
  currentStep,
  totalSteps,
  stepLabels,
  direction,
  stepKey,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  nextDisabledHint,
  isLoading,
  showBack = true,
  showFooter = true,
  topRightAction,
  variant = 'page',
  contentAlignment = 'center',
  contentClassName,
}: WizardShellProps) {
  const isMobile = useIsMobile();
  const dict = useDictionary();
  const reduceMotion = useReducedMotion();
  /** Step whose disabled CTA was tapped; the hint hides once it is enabled. */
  const [disabledTapStep, setDisabledTapStep] = useState<
    string | number | null
  >(null);
  const showDisabledHint =
    !!nextDisabledHint && !!nextDisabled && disabledTapStep === stepKey;
  const setHeaderLeading = useOnboardingHeaderLeading();
  const canNavigateBack = !!onBack && currentStep > 1;
  /** Rendered back control (header / footer). Swipe may still work when chrome is hidden. */
  const canGoBack = showBack && canNavigateBack;
  /**
   * Floating primary CTA: page onboarding only on mobile; shell wizards on
   * every viewport (same stepwise UX, breakpoint-aware stage width).
   */
  const floatingCta = variant === 'shell' || (variant === 'page' && isMobile);
  const swipe = useSwipeBack(onBack, canNavigateBack && floatingCta);

  /** Page onboarding: Back lives in {@link OnboardingLayout} beside Logout. */
  const backInPageHeader = variant === 'page' && setHeaderLeading != null;
  const setWizardFooterActive = useGuidanceStore(
    (s) => s.setWizardFooterActive,
  );
  const footerOnScreen = Boolean(
    showFooter && (onNext || (!backInPageHeader && showBack && onBack)),
  );

  useEffect(() => {
    setWizardFooterActive(footerOnScreen);
    return () => setWizardFooterActive(false);
  }, [footerOnScreen, setWizardFooterActive]);
  // Fade keeps shared layoutId morphs (CTA → form → check) from fighting a slide.
  const variants = fadeVariants;
  const transition = reduceMotion
    ? fadeTransition
    : { ...fadeTransition, duration: 0.22 };

  useEffect(() => {
    if (!backInPageHeader || !setHeaderLeading) return;
    if (!canGoBack) {
      setHeaderLeading(null);
      return () => setHeaderLeading(null);
    }
    setHeaderLeading(
      <Button
        variant="ghost"
        size={isMobile ? 'md' : 'sm'}
        onClick={onBack}
        className="text-muted-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {dict.onboarding.back}
      </Button>,
    );
    return () => setHeaderLeading(null);
  }, [
    backInPageHeader,
    setHeaderLeading,
    canGoBack,
    onBack,
    isMobile,
    dict.onboarding.back,
  ]);

  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 flex-col',
        // Shell sits under PageHeader with AppPageLayout `withGap={false}`;
        // restore Status Board section rhythm above the progress strip.
        variant === 'shell' && 'pt-[var(--app-section-gap)]',
      )}
      {...(floatingCta ? swipe : {})}
    >
      <OnboardingProgressBar
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepLabels={stepLabels}
      />

      {!backInPageHeader && (canGoBack || topRightAction) ? (
        <div className="flex items-center justify-between px-4 py-2 md:px-0 md:pb-0 md:pt-2">
          {canGoBack ? (
            <Button
              variant="ghost"
              size={isMobile ? 'md' : 'sm'}
              onClick={onBack}
            >
              <ChevronLeft className="h-5 w-5" />
              {dict.onboarding.back}
            </Button>
          ) : (
            <div />
          )}
          {topRightAction ?? <div />}
        </div>
      ) : null}

      <ScrollAreaWithShadows className="min-h-0 flex-1" resetKey={stepKey}>
        {/* my-auto: center short steps; margins collapse when content overflows. */}
        <div
          className={cn(
            'flex min-h-full flex-col',
            // Shell pages already get AppPageLayout / page gutters — avoid double
            // side padding on mobile so the stage uses the viewport. Keep a thin
            // inset so choice-card rings/borders are not clipped by overflow-y.
            // Desktop shell: no extra side pad (parent gutters); more vertical air.
            variant === 'shell'
              ? isMobile
                ? 'px-1.5 py-5'
                : 'px-0 py-6 md:py-8'
              : isMobile
                ? 'px-5 py-6'
                : 'items-center px-8 py-6',
          )}
          style={
            floatingCta && showFooter && onNext
              ? {
                  paddingBottom:
                    'calc(env(safe-area-inset-bottom, 0px) + 7rem + var(--keyboard-inset-height, 0px))',
                }
              : undefined
          }
          data-wizard-step-pad={
            floatingCta && showFooter && onNext ? '' : undefined
          }
        >
          <AnimatePresence mode="popLayout" custom={direction}>
            <motion.div
              key={stepKey}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className={cn(
                'w-full',
                contentAlignment === 'center' && 'my-auto',
                variant !== 'shell' &&
                  !isMobile &&
                  !contentClassName &&
                  'max-w-2xl',
                contentClassName,
              )}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </ScrollAreaWithShadows>

      {showFooter && onNext && floatingCta && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0"
          data-wizard-floating-cta=""
        >
          {/*
            Gold standard = create-workspace shell: CTA spans the page gutters,
            not the stage max-width. Stage `contentClassName` must not shrink
            the Continue strip (DEV-456 / products setup alignment).
          */}
          {/*
            The strip catches taps itself: a disabled button has
            pointer-events: none, so taps fell through to the choice card
            underneath and selected it (DEV-578).
          */}
          <div
            className={cn(
              'pointer-events-auto bg-background pt-2',
              variant === 'shell' ? 'mx-auto w-full px-0' : 'px-5',
            )}
            style={{
              // Safe-area + 1rem — keep defined inline (no stale/renamed binding).
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            }}
            onClick={() => {
              if (nextDisabled && !isLoading) setDisabledTapStep(stepKey);
            }}
          >
            {showDisabledHint ? (
              <p
                role="status"
                className="mb-2 text-center text-sm text-muted-foreground"
              >
                {nextDisabledHint}
              </p>
            ) : null}
            <Button
              onClick={onNext}
              disabled={nextDisabled || isLoading}
              isLoading={isLoading}
              variant="primary"
              size="lg"
              className="pointer-events-auto w-full rounded-full"
            >
              {nextLabel}
            </Button>
          </div>
        </div>
      )}

      {showFooter &&
        !floatingCta &&
        (onNext || (!backInPageHeader && showBack && onBack)) && (
          <div className="flex items-center justify-between border-t px-8 py-4">
            {!backInPageHeader && showBack && onBack && currentStep > 1 ? (
              <Button variant="outline" onClick={onBack}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                {dict.onboarding.back}
              </Button>
            ) : (
              <div />
            )}
            {onNext && (
              <Button
                onClick={onNext}
                disabled={nextDisabled || isLoading}
                isLoading={isLoading}
                variant="primary"
              >
                {nextLabel}
              </Button>
            )}
          </div>
        )}
    </div>
  );
}

function ScrollAreaWithShadows({
  children,
  className,
  resetKey,
}: {
  children: ReactNode;
  className?: string;
  resetKey?: string | number;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState<
    'top' | 'middle' | 'bottom' | 'none'
  >('none');

  const updateScrollState = useCallback((el: HTMLDivElement) => {
    const { scrollTop, scrollHeight, clientHeight } = el;
    const atTop = scrollTop <= 1;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
    if (atTop && atBottom) setScrollState('none');
    else if (atTop) setScrollState('top');
    else if (atBottom) setScrollState('bottom');
    else setScrollState('middle');
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      updateScrollState(e.currentTarget);
    },
    [updateScrollState],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    updateScrollState(el);
  }, [resetKey, updateScrollState]);

  return (
    <div className={`relative ${className ?? ''}`}>
      <div
        className="wizard-scroll-fade pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-background to-transparent transition-opacity duration-200 motion-reduce:transition-none"
        style={{
          opacity: scrollState === 'bottom' || scrollState === 'middle' ? 1 : 0,
        }}
      />
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto"
        data-wizard-inner-scroll=""
        onScroll={handleScroll}
      >
        {children}
      </div>
      <div
        className="wizard-scroll-fade pointer-events-none absolute inset-x-0 bottom-0 z-10 h-4 bg-gradient-to-t from-background to-transparent transition-opacity duration-200 motion-reduce:transition-none"
        style={{
          opacity: scrollState === 'top' || scrollState === 'middle' ? 1 : 0,
        }}
      />
    </div>
  );
}
